import { useMutation } from '@apollo/client';
import { PlanningActionType, usePlanning } from '@bechtel/client-common';
import { CANCEL_AI_PLANNING } from '@bechtel/client-gql';
import { Text, ToastType, useToastsDispatch } from '@bechtel/gds';
import { Trans } from '@lingui/macro';
import { useCallback, useRef } from 'react';

export function useCancelAiPlanningHandler() {
  const dispatchToast = useToastsDispatch();
  const { selectedForeman, planningDispatch } = usePlanning();
  const successRef = useRef<boolean>(false);

  const [cancelAiPlanning, { loading }] = useMutation(CANCEL_AI_PLANNING, {
    onCompleted: async (data) => {
      if (data?.cancelAiPlanning && !data.cancelAiPlanning.error?.length) {
        successRef.current = true;

        // Dispatch updated foreman with REJECTED status to PlanningContext
        // This triggers all dependent useEffects (planned, backlog, weekStats, expired)
        // matching the same rerender flow as foreman selection change
        planningDispatch({
          type: PlanningActionType.CrewMemberSelected,
          payload: {
            isReplan: false,
            selectedForeman: {
              ...selectedForeman,
              planningAiRequest: { ...selectedForeman?.planningAiRequest, status: 'REJECTED' },
            },
          },
        });

        dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-cancel-success' } });
        dispatchToast({
          type: 'ADD_TOAST',
          payload: {
            id: 'ai-cancel-success',
            toastType: ToastType.SUCCESS,
            content: () => (
              <Text size="500" weight="600">
                <Trans>AI planning cancelled successfully!</Trans>
              </Text>
            ),
            autoDismissTimeout: 10000, // Auto-close after 10 seconds
          },
        });
      } else {
        successRef.current = false;
        dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-cancel-error' } });
        dispatchToast({
          type: 'ADD_TOAST',
          payload: {
            id: 'ai-cancel-error',
            toastType: ToastType.WARNING,
            content: () => (
              <Text size="500" weight="600">
                <Trans>
                  {data?.cancelAiPlanning?.error?.errorCode || 'An error occurred while cancelling. Please try again.'}
                </Trans>
              </Text>
            ),
            autoDismissTimeout: 10000, // Auto-close after 10 seconds
          },
        });
      }
    },
    onError: (error) => {
      successRef.current = false;
      dispatchToast({ type: 'REMOVE_TOAST', payload: { id: 'ai-cancel-error' } });
      dispatchToast({
        type: 'ADD_TOAST',
        payload: {
          id: 'ai-cancel-error',
          toastType: ToastType.WARNING,
          content: () => (
            <Text size="500" weight="600">
              <Trans>Network error occurred. Please try again.</Trans>
            </Text>
          ),
          autoDismissTimeout: 10000, // Auto-close after 10 seconds
        },
      });
    },
  });

  const handleCancelAiPlanning = useCallback(
    async (planningAiRequestId: string): Promise<boolean> => {
      if (!planningAiRequestId) return false;

      successRef.current = false;
      try {
        await cancelAiPlanning({ variables: { planningAiRequestId } });
        return successRef.current;
      } catch (error) {
        return false;
      }
    },
    [cancelAiPlanning]
  );

  return { handleCancelAiPlanning, loading };
}
